import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { DisplayModes } from 'services/webedi/types';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import { MessageV2DTO } from 'support/types';
import { Alert } from 'components/Display/Alert/Alert';
import { AnnotationNavigationActions } from 'components/WebEDI/AnnotationNavigationActions/AnnotationNavigationActions';

import { AnnotationsLabel } from '../components/AnnotationsLabel';
import { LoadingErrors } from '../components/LoadingErrors';

type Props = {
  mode: DisplayModes;
  errors: number;
  warnings: number;
  isLoading: boolean;
  blockingError?: MessageV2DTO['blocking_error'];
  overrideContent?: ReactNode;
};

export const WithErrorPreset = ({ mode, errors, warnings, isLoading, blockingError, overrideContent }: Props) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { service } = useNavigationContext<WebEDIAnnotation>();
  // Visibility/focus-button threshold tracks what's cycleable (non-pending),
  // not what's counted by the ack-aware badges. With 1 unacked + 1 acked the
  // badge shows "1" but the Prev/Next pair must still appear so the user can
  // walk back to the acked item.
  const totalCycleableItems = service.useNumberOfCycleableItems();

  const moveToNext = () => {
    track('webedi:next_error', { nextError: service.getCurrentItem() });
    service.moveToNextItem();
  };
  const moveToPrevious = () => {
    track('webedi:previous_error', { previousError: service.getPreviousItem() });
    service.moveToPreviousItem();
  };
  const badgeKey =
    mode === DisplayModes.view ? 'webedi:viewDocument.errorBanner.badge' : 'webedi:editDocument.errorBanner.badge';
  const bodyCopy =
    mode === DisplayModes.view
      ? isLoading
        ? t('webedi:viewDocument.messageErrorBanner.loadingDatachecks.description')
        : t('webedi:viewDocument.errorBanner.fix')
      : t('webedi:editDocument.errorBanner.fix');

  return (
    <Alert withoutIcon severity="error" fullWidth className="max-h-72 overflow-y-auto">
      <div className="flex w-full items-center justify-between">
        {overrideContent ?? (
          <div>
            {blockingError?.description ? <p className="font-bold text-red-700">{blockingError.description}</p> : null}
            <p>{bodyCopy}</p>
          </div>
        )}
        {isLoading ? (
          <LoadingErrors />
        ) : (
          <AnnotationNavigationActions
            leadingContent={
              <>
                <AnnotationsLabel variant="error" label={t(badgeKey, { count: errors, numberOfErrors: errors })} />
                {warnings > 0 ? (
                  // Warnings are storybook-only for now (no production caller seeds them yet),
                  // so we reuse the viewDocument key in both modes instead of duplicating it.
                  <AnnotationsLabel
                    variant="warning"
                    label={t('webedi:viewDocument.errorBanner.warningCount', { count: warnings })}
                  />
                ) : null}
              </>
            }
            totalNumberOfAnnotations={totalCycleableItems}
            onPrev={moveToPrevious}
            onNext={moveToNext}
            prevAnalyticsId="webedi:previous_error"
            nextAnalyticsId="webedi:next_error"
            onFocus={service.resetFocus}
            focusAnalyticsId="webedi:errors_focus_first"
            focusLabel={t('webedi:viewDocument.errorBanner.focusOnError')}
          />
        )}
      </div>
    </Alert>
  );
};

/**
 * i18n keys
 * t('webedi:viewDocument.errorBanner.badge', { count, numberOfErrors })
 * t('webedi:editDocument.errorBanner.badge', { count, numberOfErrors })
 */
